import { useEffect, useState } from "react";
import { Receipt, Crown, Coins, Users } from "lucide-react";
import api from "@/lib/api";
import { Loader, Empty } from "@/components/Page";

const TYPE_META = {
  gold: { label: "Gold", Icon: Coins, cls: "text-amber-300" },
  slots: { label: "Slots", Icon: Users, cls: "text-sky-300" },
  vip: { label: "VIP", Icon: Crown, cls: "text-emerald-300" },
};

export function PurchasesPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  useEffect(() => {
    setData(null);
    api.get(`/admin/purchases?days=${days}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [days]);

  return (
    <div data-testid="admin-purchases-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Receipt size={18} className="text-emerald-400" />
          <h2 className="font-cinzel text-emerald-300 uppercase tracking-widest text-base">Purchases — Last {days} Days</h2>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`purchases-days-${d}`} onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs font-cinzel uppercase tracking-wide border rounded-sm transition-colors ${days === d ? "border-emerald-500 text-emerald-300 bg-emerald-950/30" : "border-zinc-800 text-zinc-500 hover:text-emerald-300"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {data === null ? <Loader /> : data === false ? <Empty label="Couldn't load purchases." /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 mb-5">
            <div className="border border-emerald-900/40 bg-emerald-950/10 px-4 py-3">
              <div className="font-cinzel text-2xl text-emerald-300 tabular-nums" data-testid="purchases-total">${data.total_usd.toFixed(2)}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Total Revenue</div>
            </div>
            <div className="border border-amber-900/30 bg-zinc-950 px-4 py-3">
              <div className="font-cinzel text-2xl text-amber-400 tabular-nums" data-testid="purchases-count">{data.count}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Purchases</div>
            </div>
          </div>

          {data.count === 0 ? (
            <Empty label={`No purchases in the last ${days} days.`} />
          ) : (
            <div className="border border-amber-900/30 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Lanista</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Email</th>
                  <th className="text-left px-4 py-3">Item</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-right px-4 py-3 hidden md:table-cell">When</th>
                </tr></thead>
                <tbody>
                  {data.purchases.map((p, i) => {
                    const meta = TYPE_META[p.type] || { label: p.type, Icon: Receipt, cls: "text-zinc-300" };
                    const Icon = meta.Icon;
                    return (
                      <tr key={i} data-testid={`purchase-row-${i}`} className="border-b border-zinc-900">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 ${meta.cls}`}><Icon size={13} /> {meta.label}</span>
                        </td>
                        <td className="px-4 py-3 text-zinc-100">{p.user_name || "Unknown"}</td>
                        <td className="px-4 py-3 text-zinc-500 hidden sm:table-cell">{p.email || "—"}</td>
                        <td className="px-4 py-3 text-zinc-400">{p.detail}</td>
                        <td className="px-4 py-3 text-right text-emerald-300 font-semibold tabular-nums">${Number(p.amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-zinc-500 tabular-nums hidden md:table-cell">
                          {p.at ? new Date(p.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-zinc-600 mt-3">VIP rows show newly-started subscriptions (recurring monthly renewals aren't logged locally). Amounts are in USD.</p>
        </>
      )}
    </div>
  );
}
